/**
 * @file read.ts
 * @description Чтение сохранённого payload секции (phase19.16 Р2; db-design-r2
 * §1: «API отдаёт всегда v2, витрина знает только v2»). Порядок как у
 * `readSiteTheme`:
 *   1) подъём версии (`upgradeSectionPayload`, v1 → v2);
 *   2) недостающие ключи — из умолчаний (новое поле в старом payload —
 *      не ошибка, а умолчание; молча);
 *   3) проверка схемой; не прошла — починка по полям: неизвестный вид
 *      элемента выбрасывается, неверное значение — умолчание, лишнее снято,
 *      перекрёстные правила приводятся в порядок. Всё — в `replaced`.
 * Никогда не бросает: в худшем случае отдаёт пустую секцию (витрина не падает;
 * fail fast — для записи, validate.ts).
 *
 * Р2а (db-design-r2a, ответ 2б): чтение в контексте — виды, не разрешённые в
 * контексте (данные товара на обычной странице), снимаются; повтор элемента
 * данных товара в блоке снимается; у обязательного элемента шаблона товара
 * снимается «скрыть на устройствах» (SB34). Всё — в `replaced`.
 *
 * SB120 этап 1 (db-design-sb120-stage1 §2): текущая версия — v3 (числа).
 * v2 поднимается с плотностью магазина (`density`); нет плотности при
 * явной ступени — «Обычно» и заметка в `replaced`. Починка v3: неверные
 * доли — равные, неверный порядок на телефоне снимается, наезд вместе со
 * «слить» — снимается «слить», фон колонки — по схеме заливки.
 *
 * SB120 этап 3а (db-design-sb120-stage3 §2, §4): текущая версия — v4.
 * Контекст темы для подъёма старых версий — общий объект `legacy`
 * (`{ density, headingScale }`); перед проверкой `typo` приводится к
 * канону (`compactSectionTypos`: «Авто» и пустые части не хранятся).
 * Надпись на фото по умолчанию — капсом, по центру (x/y 50).
 */

import { mergeMissingKeys } from "../site-theme/schema.js";
import { cloneJson, repairBySchema, type RepairIssue } from "../site-theme/repair.js";
import { defaultElementBody, defaultSectionLook, defaultSectionPayload, emptySectionColumn } from "./defaults.js";
import { compactSectionTypos } from "./element-typo.js";
import {
  equalShares,
  isElementHiddenSomewhere,
  isProductElementKind,
  isProductRequiredKind,
  isSectionElementKind,
  isValidPhoneOrder,
  isValidShares,
  SECTION_ELEMENT_SCHEMAS,
  SECTION_PAYLOAD_VERSION,
  sectionColumnSchema,
  sectionColumnsSchema,
  sectionElementSchema,
  sectionHeaderSchema,
  sectionLookSchema,
  sectionPayloadSchema,
  type SectionColumn,
  type SectionElement,
  type SectionLook,
  type SectionPayload,
} from "./schema.js";
import { mediaIdFromUrl, upgradeSectionPayload } from "./upgrade.js";
import type { SectionLegacyContext } from "./upgrade-v4.js";
import { isElementAllowedIn, type SectionContext } from "./contexts.js";
import { SITE_LIMITS } from "../site-theme/limits.js";

/** Что заменено при чтении (для журнала сервера и отчёта команды подъёма). */
export type SectionReadIssue = RepairIssue;

export interface SectionReadOptions {
  /** Адрес медиа v1 → id медиатеки; по умолчанию — разбор адреса медиатеки (`mediaIdFromUrl`). */
  resolveMedia?: (url: string) => string | null;
  /** Где стоит секция (Р2а); по умолчанию — обычная страница. */
  context?: SectionContext;
  /**
   * Тема, которой рисовался блок (той версии сайта, которой он принадлежит):
   * плотность и шкала заголовков — для подъёма v2/v3. Payload v4 его не требует.
   */
  legacy?: SectionLegacyContext;
  /**
   * Снято в v4 — передавайте `legacy: { density, headingScale }`. Тип `never`
   * ловит и распространение `...(d ? { density: d } : {})`, которое обычная
   * проверка лишних ключей пропускает молча (плотность потерялась бы).
   */
  density?: never;
}

export interface SectionReadResult {
  payload: SectionPayload;
  replaced: SectionReadIssue[];
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

// ── Умолчания вложенных списков и вариантов (для слияния недостающих ключей) ──

// Только обязательные поля: необязательные («как на ПК», «как у сайта») не подставляются.
const MEDIA_BG_DEFAULTS = {
  focus: { x: 50, y: 50 },
  fade: "none",
  dim: 0,
  textTone: "light",
  bleed: { top: 0, bottom: 0, side: "none" },
};
const DECOR_DEFAULTS = { x: 50, y: 50, size: 160, hideOnPhone: true, bleed: false, column: null };
const OVERLAY_DEFAULTS = { preset: "caps", x: 50, y: 50, hideOnPhone: false };
const TILE_DEFAULTS = { title: "", target: null };
const HEADER_DEFAULTS = { level: 2, line: { width: 0 } };

const mapList = (list: unknown, fn: (item: unknown) => unknown): unknown =>
  Array.isArray(list) ? list.map(fn) : list;

/** Элемент: недостающие поля — из умолчаний его вида (id не подставляется). */
function mergeElement(raw: unknown): unknown {
  if (!isPlainObject(raw) || !isSectionElementKind(raw["kind"])) return raw;
  // Цель ссылки — вариант объединения: вглубь не сливаем (к `url` не должен
  // прилипнуть `route` из умолчания), подставляем только при отсутствии.
  const { target, ...body } = defaultElementBody(raw["kind"]) as Record<string, unknown>;
  const merged = mergeMissingKeys(body, raw) as Record<string, unknown>;
  if (merged["target"] === undefined && target !== undefined) merged["target"] = target;
  if (raw["kind"] === "image") merged["overlays"] = mapList(merged["overlays"], (o) => mergeMissingKeys(OVERLAY_DEFAULTS, o));
  if (raw["kind"] === "tiles") merged["items"] = mapList(merged["items"], (t) => mergeMissingKeys(TILE_DEFAULTS, t));
  return merged;
}

/** Шаг 2: недостающие ключи на всех уровнях, где их можно восстановить однозначно. */
function mergeSection(doc: unknown): unknown {
  if (!isPlainObject(doc)) return doc;
  const defaults = defaultSectionPayload();
  // Колонки по умолчанию не подставляются поверх своих: список целиком — значение.
  const merged = mergeMissingKeys(defaults, doc) as Record<string, unknown>;
  if (isPlainObject(merged["header"])) merged["header"] = mergeMissingKeys(HEADER_DEFAULTS, merged["header"]);
  const columns = merged["columns"];
  if (isPlainObject(columns)) {
    columns["items"] = mapList(columns["items"], (col) =>
      isPlainObject(col) ? { ...col, elements: mapList(col["elements"] ?? [], mergeElement) } : col,
    );
  }
  const look = merged["look"];
  if (isPlainObject(look)) {
    if (isPlainObject(look["bg"]) && look["bg"]["kind"] === "media") {
      look["bg"] = mergeMissingKeys(MEDIA_BG_DEFAULTS, look["bg"]);
    }
    look["decor"] = mapList(look["decor"], (d) => mergeMissingKeys(DECOR_DEFAULTS, d));
  }
  return merged;
}

// ── Шаг 3: починка ──

const idSchema = SECTION_ELEMENT_SCHEMAS.heading.shape.id;

/** Повтор id в списке элемента — новый id `<префикс><номер>` по месту. */
function uniqueIds<T extends { id: string }>(list: T[], prefix: string, path: string, replaced: SectionReadIssue[]): T[] {
  const ids = new Set<string>();
  let changed = false;
  const result = list.map((item, index) => {
    if (!ids.has(item.id)) {
      ids.add(item.id);
      return item;
    }
    changed = true;
    let next = `${prefix}${index + 1}`;
    for (let n = 2; ids.has(next); n += 1) next = `${prefix}${index + 1}-${n}`;
    ids.add(next);
    return { ...item, id: next };
  });
  if (changed) replaced.push({ path, reason: "Повтор id заменён" });
  return result;
}

/** Перекрёстные правила элемента: приводим к ближайшему допустимому. null — элемент не спасти. */
function fixElementRules(el: SectionElement, path: string, replaced: SectionReadIssue[]): SectionElement | null {
  if (isProductRequiredKind(el.kind) && isElementHiddenSomewhere(el)) {
    // SB34: обязательный элемент виден везде — снимаем «скрыть», а не элемент.
    replaced.push({ path: `${path}.hide`, reason: "Обязательный элемент скрывать нельзя — показан на всех устройствах" });
    const { hide: _hide, ...rest } = el;
    return rest as SectionElement;
  }
  switch (el.kind) {
    case "products":
      if ((el.source === "badge" && el.badge === null) || (el.source === "manual" && el.ids.length === 0)) {
        replaced.push({ path: `${path}.source`, reason: "Источник товаров не настроен — показываем новинки" });
        return { ...el, source: "new" };
      }
      return el;
    case "categories":
      if (el.source === "manual" && el.ids.length === 0) {
        replaced.push({ path: `${path}.source`, reason: "Категории не выбраны — показываем по каталогу" });
        return { ...el, source: "auto" };
      }
      return el;
    case "articles":
      if (el.source === "manual" && el.ids.length === 0) {
        replaced.push({ path: `${path}.source`, reason: "Статьи не выбраны — показываем свежие" });
        return { ...el, source: "latest" };
      }
      return el;
    case "reviews":
      if (el.pick === "manual" && el.ids.length === 0) {
        replaced.push({ path: `${path}.pick`, reason: "Отзывы не выбраны — показываем лучшие" });
        return { ...el, pick: "best" };
      }
      return el;
    case "contacts": {
      const kinds = [...new Set(el.kinds)];
      if (kinds.length !== el.kinds.length) replaced.push({ path: `${path}.kinds`, reason: "Повтор контакта снят" });
      return { ...el, kinds };
    }
    case "stats":
      return { ...el, items: uniqueIds(el.items, "s", `${path}.items`, replaced) };
    case "table": {
      const columns = uniqueIds(el.columns, "c", `${path}.columns`, replaced);
      const rows = uniqueIds(el.rows, "r", `${path}.rows`, replaced).map((row, index) => {
        if (row.cells.length === columns.length) return row;
        // Ячеек не столько, сколько столбцов: дополняем пустыми или обрезаем.
        replaced.push({ path: `${path}.rows.${index}.cells`, reason: "Число ячеек приведено к числу столбцов" });
        const cells = row.cells.slice(0, columns.length);
        while (cells.length < columns.length) cells.push("");
        return { ...row, cells };
      });
      return { ...el, columns, rows };
    }
    case "announcements": {
      if (el.source === "manual" && el.items.length === 0) {
        replaced.push({ path, reason: "В строке объявлений нет ни одного объявления — элемент снят" });
        return null;
      }
      // Повтор id пункта — новый id по месту (порядок пунктов сохраняется).
      const ids = new Set<string>();
      let changed = false;
      const items = el.items.map((item, index) => {
        if (!ids.has(item.id)) {
          ids.add(item.id);
          return item;
        }
        changed = true;
        let next = `a${index + 1}`;
        for (let n = 2; ids.has(next); n += 1) next = `a${index + 1}-${n}`;
        ids.add(next);
        return { ...item, id: next };
      });
      if (changed) replaced.push({ path: `${path}.items`, reason: "Повтор id объявления заменён" });
      return { ...el, items };
    }
    default:
      return el;
  }
}

/** Починка одного элемента; null — снят (причина в `replaced`). */
function repairElement(
  raw: unknown,
  path: string,
  fallbackId: string,
  seen: Set<string>,
  replaced: SectionReadIssue[],
): SectionElement | null {
  if (!isPlainObject(raw)) {
    replaced.push({ path, reason: "Элемент не объект — снят" });
    return null;
  }
  const kind = raw["kind"];
  if (!isSectionElementKind(kind)) {
    replaced.push({ path, reason: `Неизвестный вид элемента «${String(kind)}» — снят` });
    return null;
  }
  const schema = SECTION_ELEMENT_SCHEMAS[kind];
  const fixed = repairBySchema(schema, raw, { ...defaultElementBody(kind), id: undefined }, path, replaced);
  const candidate: Record<string, unknown> = isPlainObject(fixed) ? { ...fixed } : {};
  const id = candidate["id"];
  if (typeof id !== "string" || !idSchema.safeParse(id).success || seen.has(id)) {
    let next = fallbackId;
    for (let n = 2; seen.has(next); n += 1) next = `${fallbackId}-${n}`;
    candidate["id"] = next;
    replaced.push({ path: `${path}.id`, reason: `id элемента заменён на «${next}»` });
  }
  const parsed = sectionElementSchema.safeParse(candidate);
  if (!parsed.success) {
    replaced.push({ path, reason: "Элемент не удалось исправить — снят" });
    return null;
  }
  const ruled = fixElementRules(parsed.data, path, replaced);
  if (ruled) seen.add(ruled.id);
  return ruled;
}

function repairColumn(
  raw: unknown,
  col: number,
  seen: Set<string>,
  replaced: SectionReadIssue[],
): SectionColumn {
  const path = `columns.items.${col}`;
  if (!isPlainObject(raw)) {
    replaced.push({ path, reason: "Колонка не объект — взята пустая" });
    return emptySectionColumn();
  }
  // Настройки колонки (фон-заливка, положение, отступы) — по схеме; негодное снимается.
  const { elements: _elements, ...rawSettings } = raw;
  const settingsSchema = sectionColumnSchema.omit({ elements: true });
  const settings = settingsSchema.safeParse(repairBySchema(settingsSchema, rawSettings, {}, path, replaced));
  const column: SectionColumn = { ...(settings.success ? settings.data : {}), elements: [] };
  const rawElements = Array.isArray(raw["elements"]) ? raw["elements"] : [];
  if (!Array.isArray(raw["elements"])) replaced.push({ path: `${path}.elements`, reason: "Элементы не список — колонка пустая" });
  rawElements.forEach((el, row) => {
    const element = repairElement(el, `${path}.elements.${row}`, `el-r${col + 1}-${row + 1}`, seen, replaced);
    if (element) column.elements.push(element);
  });
  if (column.elements.length > SITE_LIMITS.elementsPerColumn) {
    replaced.push({ path: `${path}.elements`, reason: `Элементов больше ${SITE_LIMITS.elementsPerColumn} — лишние сняты` });
    column.elements = column.elements.slice(0, SITE_LIMITS.elementsPerColumn);
  }
  return column;
}

/** Починка payload по частям; результат проходит схему (иначе — пустая секция). */
function repairSection(doc: unknown, replaced: SectionReadIssue[]): SectionPayload {
  if (!isPlainObject(doc)) {
    replaced.push({ path: "", reason: "Payload секции не объект — взята пустая секция" });
    return defaultSectionPayload();
  }
  for (const key of Object.keys(doc)) {
    if (!["header", "columns", "look"].includes(key)) replaced.push({ path: key, reason: "Лишнее поле снято" });
  }
  const defaults = defaultSectionPayload();

  // Колонки: сначала элементы (число колонок нужно для пропорций и рисунков).
  const rawColumns = isPlainObject(doc["columns"]) ? doc["columns"] : {};
  let rawItems = Array.isArray(rawColumns["items"]) ? rawColumns["items"] : [];
  if (rawItems.length === 0) {
    replaced.push({ path: "columns.items", reason: "Нет колонок — взята одна пустая" });
    rawItems = [emptySectionColumn()];
  }
  if (rawItems.length > 4) {
    replaced.push({ path: "columns.items", reason: "Колонок больше 4 — лишние сняты" });
    rawItems = rawItems.slice(0, 4);
  }
  const seen = new Set<string>();
  const items = rawItems.map((col, index) => repairColumn(col, index, seen, replaced));
  const settingsSchema = sectionColumnsSchema.omit({ items: true });
  const { items: _defaultItems, ...settingsDefaults } = defaults.columns;
  const { items: _rawItems, ...rawSettings } = rawColumns;
  const settings = settingsSchema.parse(
    repairBySchema(settingsSchema, rawSettings, settingsDefaults, "columns", replaced),
  );
  if (!isValidShares(settings.shares, items.length)) {
    replaced.push({ path: "columns.shares", reason: "Доли колонок не совпадали с числом колонок или не давали 12 — взяты равные" });
    settings.shares = equalShares(items.length);
  }
  if (settings.phoneOrder !== undefined && !isValidPhoneOrder(settings.phoneOrder, items.length)) {
    replaced.push({ path: "columns.phoneOrder", reason: "Порядок на телефоне не по числу колонок — по порядку" });
    delete settings.phoneOrder;
  }

  const look = sectionLookSchema.parse(
    repairBySchema(sectionLookSchema, doc["look"], defaultSectionLook(), "look", replaced),
  ) as SectionLook;
  if (look.space.overlap > 0 && look.space.mergeWithPrev) {
    replaced.push({ path: "look.space.mergeWithPrev", reason: "Наезд и слияние вместе нельзя — слияние снято" });
    look.space.mergeWithPrev = false;
  }
  look.decor.forEach((decor, index) => {
    if (decor.column !== null && decor.column > items.length) {
      replaced.push({ path: `look.decor.${index}.column`, reason: "Колонки рисунка нет — привязан к секции" });
      decor.column = null;
    }
  });

  let header: SectionPayload["header"];
  if (doc["header"] !== undefined) {
    const fixed = repairBySchema(sectionHeaderSchema, doc["header"], undefined, "header", replaced);
    const parsed = sectionHeaderSchema.safeParse(fixed);
    if (parsed.success) header = parsed.data;
    else replaced.push({ path: "header", reason: "Шапку раздела не удалось исправить — снята" });
  }

  const result = sectionPayloadSchema.safeParse({
    ...(header ? { header } : {}),
    columns: { ...settings, items },
    look,
  });
  if (result.success) return result.data;
  replaced.push({ path: "", reason: "Секцию не удалось исправить — взята пустая" });
  return defaults;
}

/**
 * Правила контекста после разбора: чужие виды и повтор элемента данных товара
 * в блоке снимаются (первый остаётся). Колонки не удаляются — пропорции целы.
 */
function applyContext(payload: SectionPayload, context: SectionContext, replaced: SectionReadIssue[]): SectionPayload {
  const seen = new Set<string>();
  let changed = false;
  const items = payload.columns.items.map((column, col) => {
    const elements = column.elements.filter((element, row) => {
      const path = `columns.items.${col}.elements.${row}`;
      if (!isElementAllowedIn(element.kind, context)) {
        replaced.push({ path, reason: `Элемент «${element.kind}» здесь не показывается — снят` });
        changed = true;
        return false;
      }
      if (!isProductElementKind(element.kind)) return true;
      if (seen.has(element.kind)) {
        replaced.push({ path, reason: `Повтор элемента «${element.kind}» в блоке — снят` });
        changed = true;
        return false;
      }
      seen.add(element.kind);
      return true;
    });
    return elements.length === column.elements.length ? column : { ...column, elements };
  });
  return changed ? { ...payload, columns: { ...payload.columns, items } } : payload;
}

/**
 * Только шаг 2 чтения — недостающие ключи из умолчаний (секция, шапка,
 * элементы по виду, надписи на фото, плитки, фон-фото, рисунки), без починки.
 * Для строгих путей, которым нужно «новое поле — умолчание, остальное —
 * ошибка»: импорт пакетов «Как было» (apps/api) дополняет и затем проверяет
 * `validateSectionPayload`. Отсутствующий ключ любого уровня дополняется
 * умолчанием; ошибкой остаётся только лишний или опечатанный ключ.
 * Вход не меняется (работаем на копии).
 */
export function fillSectionDefaults(doc: unknown): unknown {
  return mergeSection(compactSectionTypos(cloneJson(doc)));
}

/**
 * Чтение payload из БД (`SitePageBlock.payload` + `payloadVersion`): всегда
 * валидная секция текущей версии (v4). Версия новее кода — пустая секция и
 * запись в `replaced` (сервер старее данных; витрина не падает). Чтение v4
 * идемпотентно: прочитанное и прочитанное ещё раз совпадают.
 */
export function readSectionPayload(raw: unknown, version: number, opts: SectionReadOptions = {}): SectionReadResult {
  const replaced: SectionReadIssue[] = [];
  try {
    let doc = raw;
    if (version !== SECTION_PAYLOAD_VERSION) {
      if (!Number.isInteger(version) || version < 1 || version > SECTION_PAYLOAD_VERSION) {
        replaced.push({ path: "", reason: `Версия секции ${String(version)} неизвестна — взята пустая секция` });
        return { payload: defaultSectionPayload(), replaced };
      }
      const upgraded = upgradeSectionPayload(raw, version, {
        resolveMedia: opts.resolveMedia ?? mediaIdFromUrl,
        ...(opts.legacy !== undefined ? { legacy: opts.legacy } : {}),
      });
      for (const note of upgraded.notes) replaced.push({ path: "", reason: note });
      doc = upgraded.payload;
    }
    const context = opts.context ?? "page";
    const merged = mergeSection(compactSectionTypos(cloneJson(doc)));
    const direct = sectionPayloadSchema.safeParse(merged);
    if (direct.success) return { payload: applyContext(direct.data, context, replaced), replaced };
    return { payload: applyContext(repairSection(merged, replaced), context, replaced), replaced };
  } catch (error) {
    replaced.push({
      path: "",
      reason: `Секцию не удалось прочитать (${error instanceof Error ? error.message : "ошибка"}) — взята пустая`,
    });
    return { payload: defaultSectionPayload(), replaced };
  }
}
